import React, { useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSearch, faList } from '@fortawesome/free-solid-svg-icons';
import {
  fetchPracticeProviders,
  invalidatePracticeProvidersCache
} from '../../../store/slices/practiceSlice';
import { fetchProvider, clearCurrentProvider } from '../../../store/slices/providerSlice';
import { deleteProvider } from '../../../store/slices/providerSlice';
import { toast } from 'react-toastify';
import SharedDetailModal from '../../../components/Common/SharedDetailModal/SharedDetailModal';
import DetailsPage from '../../../components/Common/DetailsPage/DetailsPage';
import Table from '../../../components/Common/Table/Table';
import StatusBadge from '../../../components/Common/StatusBadge/StatusBadge';
import ActionButtons from '../../../components/Common/ActionButtons/ActionButtons';
import SearchBar from '../../../components/Common/SearchBar/SearchBar';
import '../AdminManagement/AdminManagement.css';
import './ProviderManagement.css';
import { buildDetailAddressLines, resolveStateDisplay } from '../../../utils/addressDisplay';


const ProviderManagement = ({ practiceId, isReadOnly = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    providersByPracticeId = {},
    practiceProvidersFetched = {},
    listLoading = {},
    error
  } = useSelector((state) => state.practice ?? {});
  const { 
    currentProvider, 
    isLoading: isDetailsLoading 
  } = useSelector((state) => state.provider);
  const { user } = useSelector(state => state.auth);
  const [searchTerm, setSearchTerm] = useState('');
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(30);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState(null);
  const [sortBy, setSortBy] = useState('status');
  const [sortOrder, setSortOrder] = useState('asc');

  const [searchParams] = useSearchParams();
  const viewId = searchParams.get('viewId');

  const pid = practiceId != null ? String(practiceId) : '';
  const hasCache = pid && practiceProvidersFetched[pid];
  const practiceProviders = pid ? providersByPracticeId[pid] ?? [] : [];

  useEffect(() => {
    if (practiceId && !practiceProvidersFetched[String(practiceId)]) {
      dispatch(fetchPracticeProviders(practiceId));
    }
  }, [practiceId, dispatch, practiceProvidersFetched]);

  // Debounce search term for real-time filtering
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearchTerm !== searchTerm) {
        setSearchTerm(localSearchTerm);
        setCurrentPage(1);
      }
    }, 300); // 300ms debounce delay

    return () => clearTimeout(timer);
  }, [localSearchTerm, searchTerm]);

  const filteredProviders = useMemo(() => {
    // Filter and sort providers based on search term and sort settings
    if (!practiceProviders || practiceProviders.length === 0) return [];

    let filtered = practiceProviders.filter(provider =>
      provider.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      provider.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      provider.home_phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      provider.cell_phone?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    // Sort providers
    return [...filtered].sort((a, b) => {
      if (sortBy === 'idx') {
        const ai = Number(a.id) || 0;
        const bi = Number(b.id) || 0;
        return sortOrder === 'asc' ? ai - bi : bi - ai;
      }
      if (sortBy === 'status') {
        const statusA = (a.status || 'Active').toLowerCase();
        const statusB = (b.status || 'Active').toLowerCase();

        // Custom sort: Active first, then Inactive
        const statusOrder = { 'active': 1, 'inactive': 2 };
        const orderA = statusOrder[statusA] || 3;
        const orderB = statusOrder[statusB] || 3;

        if (orderA !== orderB) {
          return sortOrder === 'asc' ? orderA - orderB : orderB - orderA;
        }
        
        // Secondary sort: Name alphabetically
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      } else if (sortBy === 'name') {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return sortOrder === 'asc'
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      } else if (sortBy === 'addr') {
        const valueA = (a.address?.address_line_1 || '').toLowerCase();
        const valueB = (b.address?.address_line_1 || '').toLowerCase();
        return sortOrder === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      } else if (sortBy === 'home') {
        const valueA = String(a.home_phone || '').toLowerCase();
        const valueB = String(b.home_phone || '').toLowerCase();
        return sortOrder === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      } else if (sortBy === 'cell') {
        const valueA = String(a.cell_phone || '').toLowerCase();
        const valueB = String(b.cell_phone || '').toLowerCase();
        return sortOrder === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      } else if (sortBy === 'fax') {
        const valueA = String(a.fax || '').toLowerCase();
        const valueB = String(b.fax || '').toLowerCase();
        return sortOrder === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      } else if (sortBy === 'email') {
        const valueA = String(a.email || '').toLowerCase();
        const valueB = String(b.email || '').toLowerCase();
        return sortOrder === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      } else {
        const valueA = a[sortBy] || '';
        const valueB = b[sortBy] || '';
        return sortOrder === 'asc'
          ? String(valueA).localeCompare(String(valueB))
          : String(valueB).localeCompare(String(valueA));
      }
    });
  }, [searchTerm, practiceProviders, sortBy, sortOrder]);

  // Reset page to 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Calculate pagination
  const entriesPerPageNum = entriesPerPage === 'all' ? filteredProviders.length : parseInt(entriesPerPage);
  const totalPages = entriesPerPage === 'all' ? 1 : Math.ceil(filteredProviders.length / entriesPerPageNum);
  const startIndex = (currentPage - 1) * entriesPerPageNum;
  const endIndex = startIndex + entriesPerPageNum;
  const currentProviders = entriesPerPage === 'all' ? filteredProviders : filteredProviders.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleEntriesChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };

  const providerStatus = (s) => {
    const v = (s || 'Active').toString().toLowerCase();
    return v === 'active' ? 'Active' : 'Inactive';
  };

  const getSafeValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'string' && value.trim() === '') return 'N/A';
    return value;
  };

  const getDisplayName = (provider) => {
    const first = provider?.first_name || '';
    const last = provider?.last_name || '';
    if (last && first) return `${last}, ${first}`;
    return (
      provider?.name ||
      `${first} ${last}`.trim() ||
      'N/A'
    );
  };

  const formatAddressLines = (entity) =>
    buildDetailAddressLines({
      line1: entity.address?.address_line_1 ?? entity.address_line_1,
      line2: entity.address?.address_line_2 ?? entity.address_line_2,
      city: entity.address?.city ?? entity.city,
      stateDisplay: resolveStateDisplay(entity),
      zip: entity.address?.zip_code ?? entity.zip_code ?? entity.zipCode
    });


  const getUsername = (entity) =>
    entity?.username ||
    entity?.user?.username ||
    entity?.login_username ||
    'N/A';

  const getTwoWayAuth = (entity) => {
    const status =
      entity?.is_two_way_auth ??
      entity?.two_way_authentication_enabled ??
      entity?.twoWayAuth ??
      entity?.two_way_auth ??
      entity?.two_way_authentication ??
      entity?.two_way_authentication_status;

    if (status === 1 || status === true || status === 'Enabled') return 'Enabled';
    if (status === 0 || status === false || status === 'Disabled') return 'Disabled';

    return 'N/A';
  };

  const handleViewProvider = (provider) => {
    const params = new URLSearchParams(window.location.search);
    params.set('viewId', provider.id);
    navigate({ search: params.toString() });
  };

  const closeProviderDetails = () => {
    dispatch(clearCurrentProvider());
    const params = new URLSearchParams(window.location.search);
    params.delete('viewId');
    navigate({ search: params.toString() });
  };

  // Lazy load provider details when viewId is present
  useEffect(() => {
    if (viewId && practiceId) {
      dispatch(fetchProvider({ practiceId, providerId: viewId }));
    }
  }, [viewId, practiceId, dispatch]);

  const handleDetailOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      closeProviderDetails();
    }
  };

  const handleEditProvider = (providerId) => {
    navigate(`/practices/${practiceId}/add-provider?editId=${providerId}`);
  };

  const handleDeleteProvider = (provider) => {
    setProviderToDelete(provider);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (providerToDelete) {
      const idToDelete = providerToDelete.id;
      try {
        // Close modal immediately; list removal is handled optimistically in practiceSlice
        setShowDeleteModal(false);
        setProviderToDelete(null);
        toast.success('Provider deleted successfully');
        await dispatch(deleteProvider({ practiceId, providerId: idToDelete })).unwrap();
      } catch (error) {
        console.error('Error deleting provider:', error);
        // If provider is already deleted (404), don't show error, just refresh
        if (error && (error.includes('already been deleted') || error.response?.status === 404 || error.message?.includes('not found'))) {
          toast.info('Provider not found or already deleted');
        } else {
          toast.error('Failed to delete provider');
        }

        // Recovery refresh only on failure to restore consistency.
        dispatch(invalidatePracticeProvidersCache(practiceId));
        await dispatch(fetchPracticeProviders(practiceId));
      }
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setProviderToDelete(null);
  };

  const handleAddProvider = () => {
    navigate(`/practices/${practiceId}/add-provider`);
  };

  const showLoading = practiceId && !hasCache && listLoading.providers;

  const handleSort = useCallback(
    (key) => {
      if (sortBy === key) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
      else {
        setSortBy(key);
        setSortOrder('asc');
      }
    },
    [sortBy]
  );

  const columns = useMemo(
    () => [
      {
        key: 'idx',
        title: '#',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (_, i) => <span>{startIndex + i + 1}</span>
      },
      {
        key: 'status',
        title: 'Status',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (row) => <StatusBadge status={providerStatus(row.status)} />
      },
      {
        key: 'name',
        title: 'Name',
        sortable: true,
        render: (row) => (
          <a
            href="#"
            className="admin-management-name-link"
            onClick={(e) => {
              e.preventDefault();
              handleViewProvider(row);
            }}
          >
            {getDisplayName(row)}
          </a>
        )
      },
      { key: 'addr', title: 'Address', sortable: true, render: (row) => row.address?.address_line_1 || 'N/A' },
      { key: 'home', title: 'Home Phone', sortable: true, render: (row) => row.home_phone || 'N/A' },
      { key: 'cell', title: 'Cell Phone', sortable: true, render: (row) => row.cell_phone || 'N/A' },
      { key: 'fax', title: 'Fax', sortable: true, render: (row) => row.fax || 'N/A' },
      { key: 'email', title: 'Email', sortable: true, render: (row) => row.email || 'N/A' },
      {
        key: 'action',
        title: 'Action',
        className: 'reusable-table-centered-col',
        render: (row) => (
          <ActionButtons
            onView={() => handleViewProvider(row)}
            onEdit={!isReadOnly ? () => handleEditProvider(row.id) : null}
            onDelete={!isReadOnly ? () => handleDeleteProvider(row) : null}
          />
        )
      }
    ],
    [startIndex, practiceId, dispatch, isReadOnly]
  );

  const providerDetails = useMemo(() => {
    // Return currentProvider if it matches viewId, else fallback to list (though list is minimal)
    if (currentProvider && String(currentProvider.id) === String(viewId)) {
      return currentProvider;
    }
    return practiceProviders.find(p => String(p.id) === String(viewId));
  }, [viewId, currentProvider, practiceProviders]);

  const staleDetailViewId =
    Boolean(viewId) &&
    Boolean(hasCache) &&
    !showLoading &&
    !isDetailsLoading &&
    (providerDetails === undefined || providerDetails === null);

  useEffect(() => {
    if (!staleDetailViewId) return;
    dispatch(clearCurrentProvider());
    const params = new URLSearchParams(window.location.search);
    params.delete('viewId');
    navigate({ search: params.toString() }, { replace: true });
  }, [staleDetailViewId, dispatch, navigate]);

  if (error && !showLoading) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  if (viewId && !staleDetailViewId) {
    return (
      <DetailsPage
        title="Practice Provider"
        data={providerDetails}
        isLoading={!providerDetails && (isDetailsLoading || showLoading)}
        config={[
          {
            placement: 'left',
            fields: [
              { label: 'Name', value: (data) => getDisplayName(data) },
              { label: 'Address', value: (data) => formatAddressLines(data), multiline: true },
              { label: 'Email', value: 'email' },
              { label: 'Home Phone', value: (data) => getSafeValue(data.home_phone || data.phone) },
              { label: 'Cell Phone', value: 'cell_phone' },
              { label: 'Fax', value: 'fax' }
            ]
          },
          {
            placement: 'right',
            fields: [
              { label: 'Username', value: (data) => getUsername(data) },
              { label: 'Two Way Authentication', value: (data) => getTwoWayAuth(data) }
            ]
          }
        ]}
      />
    );
  }

  return (
    <div className="admin-management-container">
      <div className="admin-management-header-row">
        <h2 className="admin-management-title-text">Provider List</h2>
        
        <div className="admin-management-header-actions">
          <SearchBar
            value={localSearchTerm}
            onChange={setLocalSearchTerm}
            className="admin-management-search-wrapper"
          />
          {user?.role_id !== 4 && !isReadOnly && (
            <button type="button" className="list-screen-add-btn" onClick={handleAddProvider}>
              <FontAwesomeIcon icon={faPlus} className="admin-management-add-icon" />
              Add Provider
            </button>
          )}
        </div>
      </div>

      <Table
        columns={columns}
        data={showLoading ? [] : currentProviders}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        isLoading={showLoading}
        loadingMode="spinner"
        emptyMessage="No providers found"
      />

      {/* Pagination */}
      <div className="list-screen-pagination-section">
        <div className="list-screen-pagination-left">
          <div className="list-screen-entries-selector">
            <span>Show</span>
            <select
              className="list-screen-entries-select"
              style={{ width: '80px' }}
              value={entriesPerPage}
              onChange={(e) => handleEntriesChange(e.target.value)}
            >
              <option value="10">10</option>
              <option value="30">30</option>
              <option value="50">50</option>
              <option value="all">All</option>
            </select>
            <span>entries</span>
          </div>
          <button
            type="button"
            className="list-screen-option-btn"
            onClick={() => navigate(`/event-logs/Provider/all?practiceId=${practiceId}`)}
          >
            <FontAwesomeIcon icon={faList} className="admin-management-add-icon" />
            Events Log
          </button>
        </div>

        <div className="list-screen-pagination-right">
          <div className="list-screen-page-selector">
            <span>Page</span>
            <select
              className="list-screen-page-select"
              style={{ width: '60px' }}
              value={currentPage}
              onChange={(e) => handlePageChange(parseInt(e.target.value, 10))}
            >
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <option key={page} value={page}>{page}</option>
              ))}
            </select>
            <span>of {totalPages}</span>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h5 className="modal-title">Confirm Delete</h5>
              <button
                type="button"
                className="modal-close"
                onClick={cancelDelete}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to delete the provider{' '}
                <strong>{providerToDelete?.name}</strong>?
              </p>
              <p className="modal-warning">
                This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-cancel"
                onClick={cancelDelete}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-delete"
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ProviderManagement;

const DetailRow = ({ label, value, multiline = false }) => (
  <div className="detail-row">
    <span className="detail-label">{label}</span>
    <span className="detail-separator">:</span>
    <span className={`detail-value ${multiline ? 'detail-value--multiline' : ''}`}>
      {Array.isArray(value) ? (
        value.map((line, index) => (
          <Fragment key={`${label}-${index}`}>
            {line || 'N/A'}
            {index < value.length - 1 && <br />}
          </Fragment>
        ))
      ) : (
        value || 'N/A'
      )}
    </span>
  </div>
);